/**
 * @file NeighborOS screen primitives.
 *
 * Two layers, and the split matters:
 *
 *   - `components/ui/*` is shadcn/ui — Button, Input, Label, Card, Dialog,
 *     Select, Table, Badge. Generic, accessible, and the thing to reach for when
 *     building a new screen.
 *   - this file is the app's own vocabulary: a loading spinner, an empty-state
 *     panel, an error panel, a labelled text field, an inline form error. These
 *     encode decisions specific to NeighborOS (a form error renders the API
 *     client's already-sanitised message; an empty state is never a blank area).
 *
 * The Button/Card/TextField wrappers below exist so the screens written before
 * shadcn (T0.7, T1.1) did not have to change when it arrived, and so the
 * mobile-first sizing lives in one place. New code may use either layer; prefer
 * `components/ui/*` directly for anything this file does not wrap.
 *
 * Everything here is mobile-first: full-width inputs, 16px text (below that iOS
 * Safari zooms the page on focus), and 44px tap targets — asserted in Chromium by
 * `e2e/local-properties.spec.ts` rather than described.
 */
import type { ReactNode } from "react";
import { cn } from "cn";
import { ApiError } from "../api/client";
import { Button as ShadcnButton } from "./ui/button";
import { Card as ShadcnCard } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

/** Inline spinner, sized to sit inside a line of text. */
export function Spinner() {
  return (
    <span
      role="status"
      aria-label="Loading"
      className="inline-block size-4 animate-spin rounded-full border-2 border-border border-t-primary"
    />
  );
}

/** A centred, quiet panel used for loading and empty states. */
export function CenteredPanel({ children }: { readonly children: ReactNode }) {
  return (
    <div className="flex items-center justify-center gap-3 rounded-xl border border-border bg-card px-6 py-12 text-sm text-muted-foreground">
      {children}
    </div>
  );
}

/**
 * A headline number on a screen.
 *
 * Lives here rather than inside DashboardScreen because the dashboard, the Units &
 * beds screen and the Leases screen all render metric grids. Two copies of a metric
 * card is how the same figure starts looking different on two pages.
 */
export function Metric({
  label,
  value,
  hint,
}: {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
}) {
  return (
    <ShadcnCard className="gap-1 px-4 py-4">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="text-2xl font-semibold tabular-nums tracking-tight">{value}</p>
      {hint === undefined ? null : <p className="text-xs text-muted-foreground">{hint}</p>}
    </ShadcnCard>
  );
}

/**
 * Best-effort human message from an unknown thrown value.
 *
 * Strings are handled explicitly, not just `Error`s: forms raise their own
 * validation messages as plain strings, and a `string` is not an `Error` — so it
 * fell through to "Something went wrong. Please try again.", discarding the actual
 * reason. A decimal bed count told the user nothing.
 */
function messageOf(error: unknown): string {
  if (typeof error === "string" && error.length > 0) {
    return error;
  }
  if (error instanceof Error && error.message.length > 0) {
    return error.message;
  }
  return "Something went wrong. Please try again.";
}

/** Turn a zod issue path into the field name a person would recognise. */
function fieldLabel(path: readonly (string | number)[]): string {
  if (path.length === 0) return "This form";
  const [head, ...rest] = path;
  // `units.1.label` reads as "Unit 2 → label" — index 1 is the second unit, and an
  // off-by-one here sends someone to the wrong row.
  if (head === "units" && typeof rest[0] === "number") {
    const unitNumber = rest[0] + 1;
    const field = rest.slice(1).join(".");
    return field.length === 0 ? `Unit ${String(unitNumber)}` : `Unit ${String(unitNumber)} → ${field}`;
  }
  return path.join(".");
}

/**
 * Error panel with a retry affordance. Never renders a stack trace or raw
 * response body — only the message the API client already sanitised.
 */
export function ErrorPanel({
  error,
  onRetry,
}: {
  readonly error: unknown;
  readonly onRetry: () => void;
}) {
  return (
    <div
      role="alert"
      className="rounded-xl border border-border bg-card px-6 py-8"
    >
      <h2 className="text-base font-semibold text-destructive">
        We couldn&rsquo;t load your workspace
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">{messageOf(error)}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 h-11 rounded-lg border border-border bg-card px-4 text-sm font-medium hover:border-primary"
      >
        Try again
      </button>
    </div>
  );
}

/**
 * Surface container. shadcn's Card with this app's padding as the default, and
 * `className` passed through so a caller can restyle it (dashed, tinted, tighter
 * gap) without needing a second wrapper component.
 */
export function Card({
  children,
  className,
}: {
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return <ShadcnCard className={cn("px-6 py-8", className)}>{children}</ShadcnCard>;
}

/**
 * Primary action. Thin wrapper over shadcn's Button so the mobile-first sizing
 * (44px default, see components/ui/button.tsx) applies everywhere.
 */
export function Button({
  children,
  onClick,
  type = "button",
  disabled = false,
}: {
  readonly children: ReactNode;
  readonly onClick?: () => void;
  readonly type?: "button" | "submit";
  readonly disabled?: boolean;
}) {
  return (
    <ShadcnButton type={type} disabled={disabled} onClick={onClick}>
      {children}
    </ShadcnButton>
  );
}

/** Secondary action: same tap target, quieter treatment. */
export function SecondaryButton({
  children,
  onClick,
  type = "button",
  disabled = false,
}: {
  readonly children: ReactNode;
  readonly onClick?: () => void;
  readonly type?: "button" | "submit";
  readonly disabled?: boolean;
}) {
  return (
    <ShadcnButton type={type} variant="outline" disabled={disabled} onClick={onClick}>
      {children}
    </ShadcnButton>
  );
}

/**
 * A labelled text input.
 *
 * The label is bound with `htmlFor`, so a screen reader announces it and a test
 * can find the field by name — `getByLabel` is how the E2E specs drive these
 * forms, which only works if the association is real rather than visual.
 *
 * `text-base` (16px) on the input is deliberate and load-bearing: iOS Safari
 * zooms the whole page when a focused input is smaller. shadcn's Input defaults
 * to that size already; it is restated here because the E2E asserts it.
 */
export function TextField({
  id,
  label,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder,
  maxLength,
  inputMode,
  hint,
  autoComplete,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  /**
   * `date` was added for the Expenses screen (T1.10): a cost's date is a
   * `YYYY-MM-DD` calendar day, and the browser's own date control is the honest
   * input for it (no parsing, no locale guess, no keyboard-only month picker).
   */
  readonly type?: "text" | "number" | "email" | "date" | "password";
  readonly required?: boolean;
  readonly placeholder?: string;
  readonly maxLength?: number;
  readonly inputMode?: "text" | "numeric" | "decimal";
  readonly hint?: string;
  /**
   * The browser's autofill token (`email`, `name`, `current-password`, `new-password`).
   * Set on sign-in and sign-up so a password manager fills the right field and offers to save
   * the right thing — without it, "create account" and "sign in" look identical to it.
   */
  readonly autoComplete?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id} className="text-sm font-medium">
        {label}
        {required ? (
          <span aria-hidden="true" className="ml-0.5 text-destructive">
            *
          </span>
        ) : null}
      </Label>
      <Input
        id={id}
        name={id}
        type={type}
        required={required}
        placeholder={placeholder}
        maxLength={maxLength}
        inputMode={inputMode}
        autoComplete={autoComplete}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-11 w-full min-w-0 text-base"
      />
      {hint === undefined ? null : (
        <p className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}

/**
 * The message a mutation failure should show, inline beside a form.
 *
 * Uses the API client's already-sanitised `message`; never renders a raw body.
 * `ErrorPanel` says the same thing at screen level.
 *
 * When the API rejected a specific field, the per-field problems are listed too.
 * Without them a rejected save read "invalid property", which tells a person
 * nothing about what to change — and the API had been sending the detail all along.
 */
export function FormError({ error }: { readonly error: unknown }) {
  if (error === null || error === undefined) return null;

  const issues = error instanceof ApiError ? error.issues : [];

  return (
    <div role="alert" className="flex flex-col gap-1 text-sm text-destructive">
      <p>{messageOf(error)}</p>
      {issues.length === 0 ? null : (
        <ul className="list-disc pl-5">
          {issues.map((issue, index) => (
            <li key={`${String(index)}-${issue.path.join(".")}`}>
              <span className="font-medium">{fieldLabel(issue.path)}</span>: {issue.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
